import { Button, fieldErrorsOf, PageEmpty, PageError, PageHeader, PageLoading, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useProductItems } from "../../catalog";
import { useRecordCrumb } from "../../crumb";
import { isAmount } from "../../customers";
import { messageFor } from "../../error-copy";
import { type Invoice, type InvoiceLineInput, invoiceActions, invoiceQuery } from "../../invoices";
import styles from "./invoices.module.css";
import { type Payer, PayerPicker } from "./payer";

/** One line as typed: strings throughout, sent as they are (amounts are decimal strings). */
interface LineDraft {
  key: number;
  product_item_id: string;
  description: string;
  quantity: string;
  unit_price: string;
  tax_rate: string;
}

let nextKey = 0;
const blank = (): LineDraft => ({
  key: nextKey++,
  product_item_id: "",
  description: "",
  quantity: "1",
  unit_price: "",
  tax_rate: "",
});
const fromInvoice = (i: Invoice): LineDraft[] =>
  i.lines.map((l) => ({
    key: nextKey++,
    product_item_id: l.product_item_id ?? "",
    description: l.description,
    quantity: l.quantity,
    unit_price: l.unit_price,
    tax_rate: l.tax_rate === "0.00" ? "" : l.tax_rate,
  }));

/** A line the API would take: an item or a description, a quantity and a price above zero, a rate in percent. */
export function lineProblem(l: Omit<LineDraft, "key">): string | null {
  if (!l.product_item_id && !l.description.trim()) return "Choose an item or write a description.";
  if (!/^\d{1,9}(\.\d{1,3})?$/.test(l.quantity.trim()) || /^0+(\.0+)?$/.test(l.quantity.trim()))
    return "The quantity is a number above zero.";
  if (!isAmount(l.unit_price) || /^0+(\.0+)?$/.test(l.unit_price.trim()))
    return "The unit price is an amount above zero, like 2500.00.";
  if (l.tax_rate.trim() && !/^\d{1,2}(\.\d{1,2})?$/.test(l.tax_rate.trim()))
    return "The tax rate is a percentage, like 16.";
  return null;
}

const toInput = (l: LineDraft): InvoiceLineInput => ({
  product_item_id: l.product_item_id || undefined,
  description: l.description.trim() || undefined,
  quantity: l.quantity.trim(),
  unit_price: l.unit_price.trim(),
  tax_rate: l.tax_rate.trim() || undefined,
});

/**
 * The lines of a manual invoice. Totals aren't added up here: the API works them out (rounded
 * per line) when the draft is saved, and the invoice shows those.
 */
function LinesEditor({ lines, onChange }: { lines: LineDraft[]; onChange: (lines: LineDraft[]) => void }) {
  const items = useProductItems();
  const set = (key: number, patch: Partial<LineDraft>) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  return (
    <div className={styles.section}>
      {lines.map((l, n) => {
        const problem = lineProblem(l);
        const touched = l.unit_price !== "" || l.description !== "" || l.product_item_id !== "";
        return (
          <div key={l.key} className={styles.lineGrid}>
            <div className={styles.section}>
              <Select
                label={`Line ${n + 1}: item (optional)`}
                placeholder="No item"
                value={l.product_item_id || "none"}
                onValueChange={(v) => set(l.key, { product_item_id: v === "none" ? "" : v })}
                options={[
                  { value: "none", label: "No item" },
                  ...items.items.map((i) => ({ value: i.id, label: i.label })),
                ]}
              />
              <Input
                label="Description"
                value={l.description}
                onChange={(e) => set(l.key, { description: e.target.value })}
                placeholder={l.product_item_id ? "The item's name" : "Tank cleaning"}
                error={touched && problem?.startsWith("Choose") ? problem : undefined}
              />
            </div>
            <Input
              label="Quantity"
              inputMode="decimal"
              value={l.quantity}
              onChange={(e) => set(l.key, { quantity: e.target.value })}
              error={touched && problem?.startsWith("The quantity") ? problem : undefined}
            />
            <Input
              label="Unit price"
              inputMode="decimal"
              value={l.unit_price}
              onChange={(e) => set(l.key, { unit_price: e.target.value })}
              placeholder="2500.00"
              description="Before tax"
              error={touched && problem?.startsWith("The unit price") ? problem : undefined}
            />
            <Input
              label="Tax %"
              inputMode="decimal"
              value={l.tax_rate}
              onChange={(e) => set(l.key, { tax_rate: e.target.value })}
              placeholder="0"
              error={touched && problem?.startsWith("The tax rate") ? problem : undefined}
            />
            <span />
            {lines.length > 1 ? (
              <TextButton onClick={() => onChange(lines.filter((x) => x.key !== l.key))}>Remove</TextButton>
            ) : (
              <span />
            )}
          </div>
        );
      })}
      <div>
        <TextButton onClick={() => onChange([...lines, blank()])}>Add a line</TextButton>
      </div>
    </div>
  );
}

/** A manual invoice (P8-14, `/invoices/new`): who it bills, its lines, a note. It's saved as a draft. */
export function NewInvoicePage(): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [payer, setPayer] = useState<Payer | null>(null);
  const [lines, setLines] = useState<LineDraft[]>(() => [blank()]);
  const [notes, setNotes] = useState("");
  const valid = payer && lines.every((l) => !lineProblem(l));
  const save = useMutation({
    mutationFn: () => invoiceActions.create(payer!.billee, lines.map(toInput), notes.trim() || undefined),
    onSuccess: async (draft) => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void navigate({ to: "/invoices/$invoiceId", params: { invoiceId: draft.id } });
    },
  });
  return (
    <>
      <PageHeader title="New invoice" description="Saved as a draft. Nothing is owed or sent until you issue it." />
      <div className={styles.form}>
        <PayerPicker value={payer} onChange={setPayer} />
        <LinesEditor lines={lines} onChange={setLines} />
        <Textarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        <Problems error={save.error} />
        <div className={styles.footer}>
          <Button variant="secondary" onClick={() => navigate({ to: "/invoices" })}>
            Cancel
          </Button>
          <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            Save draft
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * Edit a draft (`/invoices/$invoiceId/edit`). A draft made from deliveries keeps its lines
 * (`INVOICE_LINES_FROM_DELIVERIES`): only its note changes.
 */
export function EditInvoicePage(): ReactNode {
  const { invoiceId } = useParams({ from: "/console/invoices/$invoiceId/edit" });
  const invoice = useQuery(invoiceQuery(invoiceId));
  useRecordCrumb(invoice.data ? (invoice.data.invoice_number ?? "Draft") : undefined);
  if (invoice.status === "pending") return <PageLoading label="Loading the invoice" />;
  if (invoice.status === "error")
    return <PageError message={messageFor(errorOf(invoice.error))} onRetry={() => invoice.refetch()} />;
  if (invoice.data.status !== "draft")
    return (
      <PageEmpty
        title="This invoice is issued"
        description="An issued invoice can't change. Void it and raise a new one."
      />
    );
  return <EditDraft invoice={invoice.data} />;
}

function EditDraft({ invoice }: { invoice: Invoice }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const manual = invoice.source === "manual";
  const [lines, setLines] = useState<LineDraft[]>(() => fromInvoice(invoice));
  const [notes, setNotes] = useState(invoice.notes ?? "");
  const valid = !manual || lines.every((l) => !lineProblem(l));
  const save = useMutation({
    mutationFn: () =>
      invoiceActions.update(invoice.id, {
        ...(manual ? { lines: lines.map(toInput) } : {}),
        notes: notes.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoice", invoice.id] });
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void navigate({ to: "/invoices/$invoiceId", params: { invoiceId: invoice.id } });
    },
  });
  return (
    <>
      <PageHeader title="Edit the draft" description={`To ${invoice.billee.name}`} />
      <div className={styles.form}>
        {manual ? (
          <LinesEditor lines={lines} onChange={setLines} />
        ) : (
          <p className={styles.muted}>
            Its lines come from deliveries and stay as they are. Delete the draft to start again.
          </p>
        )}
        <Textarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        <Problems error={save.error} />
        <div className={styles.footer}>
          <Button
            variant="secondary"
            onClick={() => navigate({ to: "/invoices/$invoiceId", params: { invoiceId: invoice.id } })}
          >
            Cancel
          </Button>
          <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            Save draft
          </Button>
        </div>
      </div>
    </>
  );
}

function Problems({ error }: { error: unknown }) {
  if (!error) return null;
  const e = errorOf(error);
  const fields = Object.values(fieldErrorsOf(e));
  return (
    <Alert tone="danger" title="Not saved">
      {fields.length ? fields.join(" ") : messageFor(e)}
    </Alert>
  );
}
